import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  FiPlus, FiEdit2, FiTrash2, FiToggleLeft, FiToggleRight,
} from 'react-icons/fi';
import {
  apiAdminGetSiteContent,
  apiAdminGetSiteContentSections,
  apiToggleSiteContent,
  apiDeleteSiteContent,
  apiAdminGetPages,
  apiAdminTogglePage,
} from '../api';
import { confirmAdminAction } from '../utils/adminConfirm';
import SiteContentForm from './siteContent/SiteContentForm';

const PAGE_GROUPS = [
  { id: 'home-settings', label: 'Home Settings', prefixes: ['home_settings'], defaultSection: 'home_settings', managesPageVisibility: false },
  { id: 'shop-settings', label: 'Shop Filters', prefixes: ['shop_filters'], defaultSection: 'shop_filters', managesPageVisibility: false },
  { id: 'trust-strip', label: 'Product Trust Strip', prefixes: ['product_trust'], defaultSection: 'product_trust', managesPageVisibility: false },
  { id: 'home-offers', label: 'Home Offers', prefixes: ['home_offers'], defaultSection: 'home_offers', managesPageVisibility: false },
  { id: 'footer', label: 'Footer', prefixes: ['footer_'] },
  { id: 'about', label: 'About', prefixes: ['about_content'], defaultSection: 'about_content' },
  { id: 'contact', label: 'Contact', prefixes: ['contact_'], defaultSection: 'contact_settings' },
  { id: 'affiliate', label: 'Affiliate', prefixes: ['affiliate_'], defaultSection: 'affiliate_settings' },
  { id: 'career', label: 'Careers', prefixes: ['career_'], defaultSection: 'career_content' },
  { id: 'faq', label: 'FAQ', prefixes: ['faq_'] },
  { id: 'featured', label: 'Featured Video', prefixes: ['featured_'], defaultSection: 'featured_content' },
  { id: 'payment', label: 'Payment', prefixes: ['payment_'], defaultSection: 'payment_content' },
  { id: 'secure', label: 'Secure Shopping', prefixes: ['secure_'], defaultSection: 'secure_content' },
  { id: 'shipping', label: 'Shipping', prefixes: ['shipping_'], defaultSection: 'shipping_content' },
  { id: 'return', label: 'Returns', prefixes: ['return_'], defaultSection: 'return_content' },
  { id: 'privacy', label: 'Privacy', prefixes: ['privacy_'] },
  { id: 'terms', label: 'Terms', prefixes: ['terms_'] },
  { id: 'copyright', label: 'Copyright', prefixes: ['copyright_'] },
  { id: 'sitemap', label: 'Sitemap', prefixes: ['sitemap_'] },
  { id: 'book', label: 'Product Detail', prefixes: ['bookdetail_', 'book_detail_'] },
];

const sectionBelongsToPage = (section, pageId) => {
  if (!pageId) return true;
  const group = PAGE_GROUPS.find((entry) => entry.id === pageId);
  return group ? group.prefixes.some((prefix) => section.startsWith(prefix)) : false;
};

const sectionLabel = (section, pageId) => {
  const withoutPrefix = section.replace(new RegExp(`^${pageId}_?`), '');
  return withoutPrefix
    .split('_')
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

const tryParse = (s) => {
  try { return JSON.parse(s); } catch { return s; }
};

// Short preview of a content value (object → first 1–2 fields, string → truncated).
const previewValue = (raw) => {
  const v = tryParse(raw);
  if (v == null) return '-';
  if (typeof v === 'string') return v.length > 80 ? `${v.slice(0, 80)}...` : v;
  if (Array.isArray(v)) return `[${v.length} items]`;
  if (typeof v === 'object') {
    const entries = Object.entries(v).slice(0, 2);
    return entries.map(([k, vv]) => `${k}: ${String(vv).slice(0, 45)}`).join(' · ');
  }
  return String(v);
};

const SiteContentManager = () => {
  const [items, setItems] = useState([]);
  const [sections, setSections] = useState([]);
  const [pageStatuses, setPageStatuses] = useState([]);
  const [filterPage, setFilterPage] = useState(PAGE_GROUPS[0].id);
  const [filterSection, setFilterSection] = useState('');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [list, secs, pages] = await Promise.all([
        apiAdminGetSiteContent(filterSection || undefined),
        apiAdminGetSiteContentSections(),
        apiAdminGetPages(),
      ]);
      setItems(list.data || []);
      setSections(secs.data || []);
      setPageStatuses(pages.data || []);
    } catch { /* API client displays the error toast. */ } finally {
      setLoading(false);
    }
  }, [filterSection]);

  useEffect(() => { load(); }, [load]);

  const selectedPage = PAGE_GROUPS.find(({ id }) => id === filterPage);
  const selectedPageStatus = pageStatuses.find(({ key }) => key === filterPage);
  const pageSections = useMemo(() => {
    const existing = sections.filter((section) => sectionBelongsToPage(section, filterPage));
    if (selectedPage?.defaultSection && !existing.includes(selectedPage.defaultSection)) {
      return [selectedPage.defaultSection, ...existing];
    }
    return existing;
  }, [sections, filterPage, selectedPage]);

  useEffect(() => {
    if (pageSections.length && !pageSections.includes(filterSection)) {
      setFilterSection(pageSections[0]);
    }
  }, [filterSection, pageSections]);

  const visibleItems = useMemo(() => {
    const query = search.trim().toLowerCase();
    return items
      .filter((item) => sectionBelongsToPage(item.section, filterPage))
      .filter((item) => !query || [item.section, item.key, previewValue(item.value)]
        .some((value) => String(value).toLowerCase().includes(query)))
      .sort((a, b) => a.section.localeCompare(b.section) || a.position - b.position);
  }, [items, filterPage, search]);

  const selectPage = (pageId) => {
    setFilterPage(pageId);
    setFilterSection(sections.find((section) => sectionBelongsToPage(section, pageId)) || '');
    setSearch('');
  };

  const handleToggle = async (id) => {
    try {
      const res = await apiToggleSiteContent(id);
      setItems((prev) => prev.map((i) => (i.id === id ? res.data : i)));
    } catch { /* API client displays the error toast. */ }
  };

  const handleTogglePage = async () => {
    try {
      const response = await apiAdminTogglePage(filterPage);
      setPageStatuses((previous) => previous.map((page) => (
        page.key === filterPage ? response.data : page
      )));
    } catch { /* API client displays the error toast. */ }
  };

  const handleDelete = async (item) => {
    const confirmed = await confirmAdminAction(`Delete ${item.section} / ${item.key}?`, { title: 'Delete content', confirmLabel: 'Delete' });
    if (!confirmed) return;
    try {
      await apiDeleteSiteContent(item.id);
      setItems((prev) => prev.filter((i) => i.id !== item.id));
    } catch { /* API client displays the error toast. */ }
  };

  const handleSaved = () => {
    setShowForm(false);
    setEditing(null);
    load();
  };

  const openNewItem = () => {
    const existingSingleItem = selectedPage?.defaultSection
      ? items.find((item) => (
        item.section === selectedPage.defaultSection && item.key === 'content'
      ))
      : null;
    setEditing(existingSingleItem || null);
    setShowForm(true);
  };

  return (
    <div className="space-y-4">
      <section className="bg-white border border-gray-200 rounded-xl p-4">
        <div className="flex items-center justify-between gap-3 mb-3">
          <div>
            <h1 className="text-lg font-semibold text-black">Site Content</h1>
            <p className="text-xs text-black">Choose one page and manage only its related content.</p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {PAGE_GROUPS.map((page) => (
            <button
              type="button"
              key={page.id}
              onClick={() => selectPage(page.id)}
              className={`px-3 py-2 rounded-lg text-sm border ${filterPage === page.id
                ? 'bg-orange-600 border-orange-600 text-white'
                : 'border-gray-200 text-black hover:border-orange-300'
                }`}
            >
              {page.label}
            </button>
          ))}
        </div>
      </section>

      <div className="flex flex-wrap justify-between items-center gap-3">
        <div>
          <div className="flex items-center gap-2">
            <h2 className="font-semibold text-black">{selectedPage?.label}</h2>
            {selectedPage?.managesPageVisibility !== false && <span className={`text-xs font-medium px-2 py-1 rounded-full ${selectedPageStatus?.isActive !== false
              ? 'bg-green-100 text-green-700'
              : 'bg-gray-200 text-black'
              }`}>
              {selectedPageStatus?.isActive !== false ? 'Page Active' : 'Page Inactive'}
            </span>}
          </div>
          <p className="text-xs text-black">{visibleItems.length} content items</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {selectedPage?.managesPageVisibility !== false && <button
            type="button"
            onClick={handleTogglePage}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium border ${selectedPageStatus?.isActive !== false
              ? 'border-red-200 text-red-600 hover:bg-red-50'
              : 'border-green-200 text-green-700 hover:bg-green-50'
              }`}
          >
            {selectedPageStatus?.isActive !== false
              ? <FiToggleRight size={19} />
              : <FiToggleLeft size={19} />}
            {selectedPageStatus?.isActive !== false ? 'Deactivate Page' : 'Activate Page'}
          </button>}
          <button
            onClick={openNewItem}
            className="flex items-center gap-2 bg-orange-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-orange-700"
          >
            <FiPlus size={16} /> {selectedPage?.defaultSection && items.some((item) => (
              item.section === selectedPage.defaultSection && item.key === 'content'
            )) ? `Edit ${selectedPage.label} Content` : `New ${selectedPage?.label} Item`}
          </button>
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-xl p-3 space-y-3">
        <div className="flex flex-wrap gap-2" aria-label={`${selectedPage?.label} sections`}>
          {pageSections.map((section) => (
            <button
              key={section}
              type="button"
              onClick={() => { setFilterSection(section); setSearch(''); }}
              className={`px-3 py-1.5 rounded-lg border text-xs font-medium ${filterSection === section
                ? 'bg-gray-900 border-gray-900 text-white'
                : 'border-gray-200 text-black hover:border-orange-300'
                }`}
            >
              {sectionLabel(section, filterPage)}
            </button>
          ))}
        </div>
        <input
          type="search"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Search key or content..."
          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
        />
      </div>

      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin w-8 h-8 border-4 border-orange-500 border-t-transparent rounded-full mx-auto"></div>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-black text-xs uppercase">
              <tr>
                <th className="px-4 py-3 text-left">Section</th>
                <th className="px-4 py-3 text-left">Key</th>
                <th className="px-4 py-3 text-left">Value Preview</th>
                <th className="px-4 py-3 text-center hidden md:table-cell">Position</th>
                <th className="px-4 py-3 text-center">Status</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {visibleItems.map((item) => (
                <tr key={item.id} className="border-t border-gray-100 hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <span className="font-mono text-xs bg-gray-100 px-2 py-0.5 rounded">
                      {item.section}
                    </span>
                  </td>
                  <td className="px-4 py-3 font-medium text-black">{item.key}</td>
                  <td className="px-4 py-3 text-black text-xs truncate max-w-md">
                    {previewValue(item.value)}
                  </td>
                  <td className="px-4 py-3 text-center text-black hidden md:table-cell">
                    {item.position}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className={`text-xs font-medium px-2 py-1 rounded-full ${item.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-black'
                      }`}>
                      {item.isActive ? 'Active' : 'Hidden'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-3">
                      <button
                        onClick={() => handleToggle(item.id)}
                        className="text-black hover:text-orange-600"
                        title={item.isActive ? 'Hide' : 'Show'}
                      >
                        {item.isActive
                          ? <FiToggleRight size={20} className="text-green-600" />
                          : <FiToggleLeft size={20} />}
                      </button>
                      <button
                        onClick={() => { setEditing(item); setShowForm(true); }}
                        className="text-blue-500 hover:text-blue-700" title="Edit"
                      >
                        <FiEdit2 size={16} />
                      </button>
                      <button
                        onClick={() => handleDelete(item)}
                        className="text-red-500 hover:text-red-700" title="Delete"
                      >
                        <FiTrash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {visibleItems.length === 0 && (
            <div className="text-center py-12 text-black">No content items</div>
          )}
        </div>
      )}

      {showForm && (
        <SiteContentForm
          item={editing}
          sections={sections}
          allowedSections={pageSections}
          defaultSection={filterSection || selectedPage?.defaultSection || pageSections[0] || ''}
          onClose={() => { setShowForm(false); setEditing(null); }}
          onSaved={handleSaved}
        />
      )}
    </div>
  );
};

export default SiteContentManager;
